/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { userProfileApi } from '../../services/api';
import type { UserProfile, OnboardingData, NutritionTargets } from '../../types';

interface UserProfileState {
  profile: UserProfile | null;
  nutritionTargets: NutritionTargets | null;
  isLoading: boolean;
  targetsLoading: boolean;
  error: string | null;
  onboardingStep: number;
}

const initialState: UserProfileState = {
  profile: null,
  nutritionTargets: null,
  isLoading: true,  // true so OnboardingCheck waits for the first getProfile() before redirecting
  targetsLoading: false,
  error: null,
  onboardingStep: 0,
};

// Async thunks
export const getProfile = createAsyncThunk(
  'userProfile/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const profile = await userProfileApi.getProfile();
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get profile');
    }
  }
);

export const createProfile = createAsyncThunk(
  'userProfile/createProfile',
  async (data: OnboardingData, { rejectWithValue }) => {
    try {
      const profile = await userProfileApi.createProfile(data);
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to create profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'userProfile/updateProfile',
  async (data: Partial<OnboardingData>, { rejectWithValue }) => {
    try {
      const profile = await userProfileApi.updateProfile(data);
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to update profile');
    }
  }
);

export const getNutritionTargets = createAsyncThunk(
  'userProfile/getNutritionTargets',
  async (_, { rejectWithValue }) => {
    try {
      const targets = await userProfileApi.getNutritionTargets();
      return targets;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get nutrition targets');
    }
  }
);

// Slice
const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setOnboardingStep: (state, action) => {
      state.onboardingStep = action.payload;
    },
    nextOnboardingStep: (state) => {
      state.onboardingStep += 1;
    },
    previousOnboardingStep: (state) => {
      state.onboardingStep = Math.max(0, state.onboardingStep - 1);
    },
    resetOnboarding: (state) => {
      state.onboardingStep = 0;
    },
  },
  extraReducers: (builder) => {
    // Get profile
    builder.addCase(getProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      state.profile = action.payload;
      state.error = null;
    });
    builder.addCase(getProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Create profile
    builder.addCase(createProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      state.profile = action.payload;
      state.error = null;
    });
    builder.addCase(createProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Update profile
    builder.addCase(updateProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      state.profile = action.payload;
      state.error = null;
    });
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get nutrition targets — uses its own loading flag so it never trips the
    // global OnboardingCheck gate (which keys off `isLoading`). Sharing isLoading
    // here caused an infinite mount/unmount loop on pages that fetch targets.
    builder.addCase(getNutritionTargets.pending, (state) => {
      state.targetsLoading = true;
    });
    builder.addCase(getNutritionTargets.fulfilled, (state, action) => {
      state.targetsLoading = false;
      state.nutritionTargets = action.payload;
    });
    builder.addCase(getNutritionTargets.rejected, (state, action) => {
      state.targetsLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  clearError,
  setOnboardingStep,
  nextOnboardingStep,
  previousOnboardingStep,
  resetOnboarding,
} = userProfileSlice.actions;

export default userProfileSlice.reducer;
